// The EVIDENCE blocks (brief §5.3.4): one per declared binding — source label, item-scope pulled-at
// stamp with the honest staleness flag, the projection-shaped rows (key-figure pairs render as a
// typed table, message-shaped rows as stacked entries with the projection-authored attention flag),
// and the stored raw-snapshot reference line (served elsewhere under deny-by-default rules, G-031).
// The card renders EXACTLY the snapshots the worker reasoned over (P-SELF — same bytes, one source
// of truth); nothing is recomputed or invented here. Tokens only (G-181).

import type { EvidenceBlock } from '../projections.ts';
import { Dot } from '../../primitives.tsx';
import { WorkSectionHead } from '../work-ui.tsx';

type EvidenceRow = Readonly<Record<string, string | number | null>>;

/** True when every row is a key-figure pair (`field` → `value`) — the typed-table shape. */
function isKvRows(rows: readonly EvidenceRow[]): boolean {
  return (
    rows.length > 0 &&
    rows.every((row) => {
      const keys = Object.keys(row);
      return keys.length === 2 && keys.includes('field') && keys.includes('value');
    })
  );
}

function display(value: string | number | null): string {
  return value === null ? '' : String(value);
}

/** The key-figure table (the invoice/contact-history shape). */
function KvRows({ rows }: { readonly rows: readonly EvidenceRow[] }): React.JSX.Element {
  return (
    <div className="ritona-numeric grid grid-cols-[auto_1fr] gap-x-5 gap-y-1.5 text-xs">
      {rows.map((row, index) => (
        <div key={index} className="contents">
          <span className="text-text-subtle">{display(row['field'] ?? null)}</span>
          <span className="text-text">{display(row['value'] ?? null)}</span>
        </div>
      ))}
    </div>
  );
}

/** Stacked message-shaped rows; a flagged row (the dispute mail) carries the attention edge. */
function StackedRows({
  rows,
  flagged,
}: {
  readonly rows: readonly EvidenceRow[];
  readonly flagged: readonly number[];
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      {rows.map((row, index) => {
        const isFlagged = flagged.includes(index);
        const entries = Object.entries(row);
        const head = entries[0];
        return (
          <div
            key={index}
            data-flagged={isFlagged ? 'true' : 'false'}
            className={`border-l-2 py-0.5 pl-3 ${isFlagged ? 'border-bracken' : 'border-border'}`}
          >
            {head !== undefined ? (
              <div className="ritona-numeric text-xs text-text-muted">{display(head[1])}</div>
            ) : null}
            {entries.slice(1).map(([key, value]) => (
              <p key={key} className="mt-0.5 text-sm leading-relaxed text-text">
                {display(value)}
              </p>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/** One binding's snapshot block: provenance head, rows, and the raw-snapshot reference line. */
function EvidenceBlockView({ block }: { readonly block: EvidenceBlock }): React.JSX.Element {
  return (
    <div data-binding={block.bindingKey} className="border-t border-border py-4 first:border-t-0">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-3">
        <span className="ritona-numeric text-xs font-medium text-text">{block.sourceLabel}</span>
        <span className="ritona-numeric flex items-center gap-2 text-xs uppercase text-text-subtle">
          item · {block.pulledAt}
          {block.stale ? (
            <span data-stale="true" className="flex items-center gap-1 text-bracken">
              <Dot tone="attention" /> stale
            </span>
          ) : null}
        </span>
      </div>
      {isKvRows(block.rows) ? (
        <KvRows rows={block.rows} />
      ) : (
        <StackedRows rows={block.rows} flagged={block.flaggedRows} />
      )}
      <div
        data-artifact={block.artifactRef}
        className="ritona-numeric mt-2.5 text-xs text-text-subtle"
      >
        raw snapshot · {block.artifactRef} · kept forever, append-only
      </div>
    </div>
  );
}

/** The evidence section — the declared item-scoped pulls, one block per binding. */
export function EvidenceList({
  evidence,
}: {
  readonly evidence: readonly EvidenceBlock[];
}): React.JSX.Element {
  return (
    <section aria-label="Evidence" className="mt-8">
      <WorkSectionHead
        label="Evidence"
        source="the declared item-scoped pulls — the same snapshots the worker reasoned over"
      />
      {evidence.map((block) => (
        <EvidenceBlockView key={block.bindingKey} block={block} />
      ))}
    </section>
  );
}
